import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronDown,
  Play,
  Users,
  Volume2,
  VolumeX,
} from 'lucide-react';
import type { useVoiceOutput, VoiceGender } from '../hooks/useVoiceOutput';

type VoiceOut = ReturnType<typeof useVoiceOutput>;

const SAMPLE = 'Hola, soy Intraeco. ¿En qué puedo ayudarte hoy?';

/**
 * Mapeo de país en inglés → español, para mostrar el origen de la voz
 * de forma más amigable al usuario.
 */
const COUNTRY_ES: Record<string, string> = {
  'Spain': 'España',
  'Argentina': 'Argentina',
  'Bolivia': 'Bolivia',
  'Chile': 'Chile',
  'Colombia': 'Colombia',
  'Costa Rica': 'Costa Rica',
  'Cuba': 'Cuba',
  'Dominican Republic': 'Rep. Dominicana',
  'Ecuador': 'Ecuador',
  'El Salvador': 'El Salvador',
  'Guatemala': 'Guatemala',
  'Honduras': 'Honduras',
  'Mexico': 'México',
  'Nicaragua': 'Nicaragua',
  'Panama': 'Panamá',
  'Paraguay': 'Paraguay',
  'Peru': 'Perú',
  'Puerto Rico': 'Puerto Rico',
  'United States': 'Estados Unidos',
  'Uruguay': 'Uruguay',
  'Venezuela': 'Venezuela',
};

const COUNTRY_BY_LANG: Record<string, string> = {
  'es-ES': 'España',
  'es-MX': 'México',
  'es-AR': 'Argentina',
  'es-CL': 'Chile',
  'es-CO': 'Colombia',
  'es-PE': 'Perú',
  'es-VE': 'Venezuela',
  'es-EC': 'Ecuador',
  'es-BO': 'Bolivia',
  'es-PY': 'Paraguay',
  'es-UY': 'Uruguay',
  'es-CR': 'Costa Rica',
  'es-CU': 'Cuba',
  'es-DO': 'Rep. Dominicana',
  'es-SV': 'El Salvador',
  'es-GT': 'Guatemala',
  'es-HN': 'Honduras',
  'es-NI': 'Nicaragua',
  'es-PA': 'Panamá',
  'es-PR': 'Puerto Rico',
  'es-US': 'Estados Unidos',
};

/** Extrae nombre limpio + país. Ej: "Microsoft Isidora Multilingual Online (Natural) - Spanish (Spain)" → { name: "Isidora", country: "España" } */
function prettifyVoice(voice: SpeechSynthesisVoice): { name: string; country: string } {
  let raw = voice.name;

  // Quitar prefijo Microsoft / Google / Apple
  raw = raw.replace(/^(Microsoft|Google|Apple)\s+/i, '');

  // El nombre suele ser la primera palabra (Isidora, Elena, Pablo, Alvaro...)
  const firstWord = raw.split(/\s+/)[0] ?? raw;

  // País entre paréntesis al final: "(Spain)", "(Argentina)", etc.
  const countryMatch = voice.name.match(/\(([^)]+)\)\s*$/);
  let country = '';
  if (countryMatch) {
    const englishCountry = countryMatch[1];
    country = COUNTRY_ES[englishCountry] ?? englishCountry;
  } else {
    // Fallback: usar el lang BCP-47
    country = COUNTRY_BY_LANG[voice.lang] ?? voice.lang.toUpperCase();
  }

  return { name: firstWord, country };
}

export function VoiceSettings({ voice }: { voice: VoiceOut }) {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Cerrar al hacer click fuera
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Lista de voces con su nombre/país ya formateado
  const formattedVoices = useMemo(
    () => voice.browserVoices.map((v) => ({ voice: v, ...prettifyVoice(v) })),
    [voice.browserVoices],
  );

  const Icon = voice.enabled ? Volume2 : VolumeX;

  return (
    <div className="relative" ref={panelRef}>
      <div className="flex items-center gap-1">
        <button
          onClick={voice.toggleEnabled}
          className={`p-2 rounded-l-xl border transition ${
            voice.enabled
              ? 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100'
              : 'bg-slate-100 border-slate-200 text-slate-500 hover:bg-slate-200'
          }`}
          title={voice.enabled ? 'Voz activada (clic para silenciar)' : 'Voz silenciada (clic para activar)'}
        >
          <Icon size={18} />
        </button>
        <button
          onClick={() => setOpen((o) => !o)}
          className={`p-2 rounded-r-xl border border-l-0 transition ${
            voice.enabled
              ? 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100'
              : 'bg-slate-100 border-slate-200 text-slate-500 hover:bg-slate-200'
          }`}
          title="Ajustes de voz"
        >
          <ChevronDown size={14} className={`transition ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 rounded-2xl border border-slate-200 bg-white shadow-xl z-30 p-4">
          <div className="text-xs font-black uppercase tracking-widest text-slate-500 mb-3">
            Ajustes de voz
          </div>

          {/* Género */}
          <div className="mb-4">
            <div className="text-xs font-bold text-slate-600 mb-2 flex items-center gap-1">
              <Users size={12} /> Género
            </div>
            <div className="grid grid-cols-3 gap-2">
              {(
                [
                  { key: 'any',    label: 'Cualquiera', icon: '🔀' },
                  { key: 'female', label: 'Femenina',   icon: '🚺' },
                  { key: 'male',   label: 'Masculina',  icon: '🚹' },
                ] as { key: VoiceGender; label: string; icon: string }[]
              ).map((g) => (
                <button
                  key={g.key}
                  onClick={() => voice.setGender(g.key)}
                  className={`flex flex-col items-center gap-0.5 rounded-xl border px-2 py-2 text-[11px] transition ${
                    voice.gender === g.key
                      ? 'border-emerald-400 bg-emerald-50 text-emerald-800 font-bold'
                      : 'border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <span className="text-base leading-none">{g.icon}</span>
                  {g.label}
                </button>
              ))}
            </div>
          </div>

          {/* Voces */}
          <div className="mb-4">
            <div className="text-xs font-bold text-slate-600 mb-2">
              Voz ({formattedVoices.length} disponibles)
            </div>
            {formattedVoices.length === 0 ? (
              <div className="text-xs text-slate-500 italic">
                No hay voces en español instaladas en el sistema.
              </div>
            ) : (
              <select
                value={voice.browserVoiceName}
                onChange={(e) => voice.setBrowserVoiceName(e.target.value)}
                className="w-full rounded-lg border border-slate-300 bg-white px-2 py-2 text-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200"
              >
                {formattedVoices.map(({ voice: v, name, country }) => (
                  <option key={v.name} value={v.name}>
                    {name} · {country}
                  </option>
                ))}
              </select>
            )}
            <div className="mt-1 text-[10px] text-slate-400">
              Tip: en Edge tienes las voces «Online (Natural)» de Microsoft con mejor calidad.
            </div>
          </div>

          <button
            onClick={() => voice.speak(SAMPLE)}
            disabled={!voice.enabled || voice.speaking}
            className="w-full flex items-center justify-center gap-2 rounded-xl bg-emerald-700 hover:bg-emerald-800 disabled:bg-slate-300 disabled:cursor-not-allowed px-3 py-2 text-sm font-bold text-white transition"
          >
            <Play size={14} /> {voice.speaking ? 'Reproduciendo…' : 'Probar voz'}
          </button>
        </div>
      )}
    </div>
  );
}
